export type PgmLevelOption = {
  code: string;
  label: string;
  primary: boolean;
};

const PRIMARY_TRACKS: Record<string, string[]> = {
  STUDENT: ["V0", "V1", "V2", "V3"],
  PROSPECT: ["F0"],
  AFFILIATE: ["A0"],
  ASSOCIATE: ["F0", "F1", "FE2", "FE3"],
  MEMBER: ["F0", "F1", "FE2", "FE3"]
};

const EXTRA_LEVELS: Record<string, string[]> = {
  STUDENT: ["F0", "A0"],
  PROSPECT: ["A0"],
  AFFILIATE: ["F0"],
  ASSOCIATE: [],
  MEMBER: []
};

const DEFAULT_COMPLETED: Record<string, string[]> = {
  STUDENT: ["V0"],
  PROSPECT: ["F0"],
  AFFILIATE: ["A0"],
  ASSOCIATE: ["F0", "F1", "FE2", "FE3"],
  MEMBER: ["F0", "F1", "FE2", "FE3"]
};

const LEVEL_LABELS: Record<string, string> = {
  V0: "V0 — Student Level 0",
  V1: "V1 — Student Level 1",
  V2: "V2 — Student Level 2",
  V3: "V3 — Student Level 3",
  F0: "F0 — Qualifying Level complete (Course Manuals)",
  A0: "A0 — Qualifying Level complete (Course Manuals)",
  F1: "F1 — Associate Level 1",
  FE2: "FE2 — Associate Level 2 (Executive)",
  FE3: "FE3 — Associate Level 3 (Executive)"
};

export function allowsIncompleteQualifyingLevel(programType: string): boolean {
  return programType === "PROSPECT" || programType === "ASSOCIATE" || programType === "AFFILIATE";
}

export function defaultCompletedPgmLevels(programType: string): string[] {
  return [...(DEFAULT_COMPLETED[programType] ?? [])];
}

export function pgmLevelOptionsForProgram(programType: string): PgmLevelOption[] {
  const primary = PRIMARY_TRACKS[programType] ?? [];
  const extras = EXTRA_LEVELS[programType] ?? [];

  return [
    ...primary.map((code) => ({
      code,
      label: LEVEL_LABELS[code] ?? code,
      primary: true
    })),
    ...extras.map((code) => ({
      code,
      label: LEVEL_LABELS[code] ?? code,
      primary: false
    }))
  ];
}

export function isQualifyingLevelCompleteSelection(programType: string, completedCodes: string[]): boolean {
  const primary = PRIMARY_TRACKS[programType] ?? [];
  const firstPrimary = primary[0];
  if (!firstPrimary || !["F0", "A0"].includes(firstPrimary)) {
    return completedCodes.some((code) => code === "F0" || code === "A0");
  }
  return completedCodes.includes(firstPrimary);
}

/** Contiguous primary-track selection: toggling a primary level selects/deselects the prefix through that level. */
export function toggleCompletedPgmLevel(programType: string, current: string[], code: string): string[] {
  const primary = PRIMARY_TRACKS[programType] ?? [];
  const extras = EXTRA_LEVELS[programType] ?? [];
  const selected = new Set(current);

  if (extras.includes(code)) {
    if (selected.has(code)) {
      selected.delete(code);
    } else {
      selected.add(code);
    }
    const next = orderCompletedCodes(programType, [...selected]);
    if (allowsIncompleteQualifyingLevel(programType)) {
      return next;
    }
    // Keep at least one completed level so the form state matches what we submit.
    return next.length > 0 ? next : current;
  }

  const index = primary.indexOf(code);
  if (index < 0) {
    return current;
  }

  const currentlySelectedPrimary = primary.filter((level) => selected.has(level));
  const isHighestSelected = currentlySelectedPrimary.at(-1) === code;

  if (isHighestSelected) {
    // Deselect this level and everything after it (prefix through previous).
    primary.slice(index).forEach((level) => selected.delete(level));
  } else {
    // Select contiguous prefix through this level.
    primary.slice(0, index + 1).forEach((level) => selected.add(level));
    primary.slice(index + 1).forEach((level) => selected.delete(level));
  }

  const next = orderCompletedCodes(programType, [...selected]);
  if (allowsIncompleteQualifyingLevel(programType)) {
    return next;
  }

  // Keep at least one completed level so the form state matches what we submit.
  return next.length > 0 ? next : current;
}

function orderCompletedCodes(programType: string, codes: string[]): string[] {
  const primary = PRIMARY_TRACKS[programType] ?? [];
  const extras = EXTRA_LEVELS[programType] ?? [];
  const set = new Set(codes);

  return [...primary.filter((code) => set.has(code)), ...extras.filter((code) => set.has(code))];
}
